import styled from 'styled-components';
import Input from '../Input/Input';
import { ISelectOption, Select } from '../Select/Select';
import { useCallback, useEffect, useState } from 'react';
import { Label } from '../Label/Label';

const Wrapper = styled.div`
  width: 250px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing2};
`;

const InputsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.designToken.spacing1};
  margin-bottom: -${({ theme }) => theme.designToken.spacing4};
`;

export type LookBackMeasurement = 'hours' | 'days';

export interface LookBackPeriodProps extends IntrinsicProps {
  label: string;
  tooltipHeading: string;
  tooltipDescription: string;
  onChange(value: number, measurement: LookBackMeasurement): void;
  valueAriaLabel?: string;
  measurementAriaLabel?: string;
  defaultValue?: number;
  defaultMeasurement?: LookBackMeasurement;
  isBold?: boolean;
}

const MINIMUM_VALUE = 1;
const MAX_HOURS = 720;
const MAX_DAYS = 30;

const measurements: ISelectOption<LookBackMeasurement>[] = [
  { label: 'Hours', value: 'hours' },
  { label: 'Days', value: 'days' },
];

export const LookBackPeriod = ({
  defaultValue = 24,
  defaultMeasurement = 'hours',
  valueAriaLabel,
  measurementAriaLabel,
  label,
  tooltipHeading,
  tooltipDescription,
  isBold = false,
  onChange,
  ...props
}: LookBackPeriodProps) => {
  const [value, setValue] = useState(defaultValue);
  const [selectedMeasurement, setSelectedMeasurement] = useState<ISelectOption<LookBackMeasurement>>(
    measurements.find(s => s.value === defaultMeasurement) || measurements[0],
  );

  useEffect(() => {
    setValue(defaultValue);
    setSelectedMeasurement(measurements.find(s => s.value === defaultMeasurement) || measurements[0]);
  }, [defaultValue, defaultMeasurement]);

  const sanitiseValue = (numValue: number, measurement: LookBackMeasurement) => {
    if (measurement === 'hours') {
      return numValue < MINIMUM_VALUE ? MINIMUM_VALUE : numValue > MAX_HOURS ? MAX_HOURS : numValue;
    } else if (measurement === 'days') {
      return numValue < MINIMUM_VALUE ? MINIMUM_VALUE : numValue > MAX_DAYS ? MAX_DAYS : numValue;
    }
    return MINIMUM_VALUE;
  };

  const onValueChange = useCallback(
    (value: string) => {
      const numValue = Number(value);
      const sanitisedValue = sanitiseValue(numValue, selectedMeasurement.value);
      setValue(sanitisedValue);
      onChange(sanitisedValue, selectedMeasurement.value);
    },
    [onChange, selectedMeasurement.value],
  );

  const onMeasurementChange = useCallback(
    (option: ISelectOption<LookBackMeasurement>) => {
      const sanitisedValue = sanitiseValue(value, option.value);
      setSelectedMeasurement(option);
      setValue(sanitisedValue);
      onChange(sanitisedValue, option.value);
    },
    [onChange, value],
  );

  return (
    <Wrapper>
      <Label isBold={isBold} name={label} tooltipHeading={tooltipHeading} tooltipDescription={tooltipDescription} />
      <InputsWrapper {...props}>
        <Input
          data-testid="lookbackPeriodInput"
          aria-label={valueAriaLabel}
          type="number"
          onChange={onValueChange}
          min={MINIMUM_VALUE + ''}
          value={value}
          maxWidth="125px"
        />
        <Select
          ariaLabel={measurementAriaLabel}
          onChange={onMeasurementChange}
          value={selectedMeasurement}
          options={measurements}
          spacingBelow={false}
          clearable={false}
          inline
        />
      </InputsWrapper>
    </Wrapper>
  );
};
